import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
} from "@mui/material";
import { useUserInfoQuery } from "Auth/Api/Queries/useUserInfoQuery";
import { useCreateExtractionConfigurationMutation } from "Configurations/Api/Mutations/useCreateExtractionConfigurationMutation";
import { useUpdateExtractionConfigurationMutation } from "Configurations/Api/Mutations/useUpdateExtractionConfigurationMutation";
import { JsonEditorDialog } from "Configurations/Components/JsonEditorDialog";
import { useExtractionConfigurationsQuery } from "Configurations/Api/Queries/useExtractionConfigurationsQuery";
import { useConnectorConfigurationsQuery } from "ConnectorConfigurations/Api/Queries/useConnectorConfigurationsQuery";
import type {
	ConnectorConfigurationListItemDto,
	ExtractionConfigurationListItemDto,
	TenantListItemDto,
} from "Infrastructure/Api/Api";
import { useTenantsQuery } from "Tenants/Api/Queries/useTenantsQuery";
import { type FC, useEffect, useRef, useState } from "react";

type Props = {
	open: boolean;
	onClose: () => void;
	editData?: ExtractionConfigurationListItemDto;
};

export const CreateExtractionConfigurationDialog: FC<Props> = ({
	open,
	onClose,
	editData,
}) => {
	const [name, setName] = useState("");
	const [prompt, setPrompt] = useState("");
	const [jsonSchema, setJsonSchema] = useState("");
	const [identificationHints, setIdentificationHints] = useState("");
	const [connectorConfigurationId, setConnectorConfigurationId] = useState<
		number | ""
	>("");
	const [tenantId, setTenantId] = useState<number | "">("");
	const [pageLimit, setPageLimit] = useState<string>("");
	const [forceOcr, setForceOcr] = useState(true);
	const [isFallback, setIsFallback] = useState(false);
	const [parentConfigurationId, setParentConfigurationId] = useState<number | "">("");
	const [errors, setErrors] = useState({
		name: "",
		prompt: "",
		jsonSchema: "",
		connectorConfigurationId: "",
		tenantId: "",
	});
	const [jsonEditorOpen, setJsonEditorOpen] = useState(false);
	const [tenantIdSet, setTenantIdSet] = useState(false);

	const createMutation = useCreateExtractionConfigurationMutation();
	const updateMutation = useUpdateExtractionConfigurationMutation();
	const connectorConfigsQuery = useConnectorConfigurationsQuery();
	const userInfoQuery = useUserInfoQuery();

	const isAdmin =
		userInfoQuery.data?.status === 200 &&
		userInfoQuery.data.data.accessRightCodes?.includes("Tenants");
	const isEditMode = !!editData;

	// Seznam konektoru vraci jen aktivni, takze hodnota, ktera v nem neni, ukazuje na smazany.
	const connectorIsDeleted =
		connectorConfigurationId !== "" &&
		(connectorConfigsQuery.data?.items ?? []).length > 0 &&
		!(connectorConfigsQuery.data?.items ?? []).some(
			(c) => c.connectorConfigurationID === connectorConfigurationId,
		);

	// Nabidka rodicu: konfigurace tehoz tenanta bez teto a bez jejich potomku,
	// aby ve stromu neslo vyrobit smycku.
	const allConfigsQuery = useExtractionConfigurationsQuery();
	const parentOptions = (() => {
		const all = allConfigsQuery.data?.items ?? [];
		const sameTenant = editData
			? all.filter((c) => c.tenantCode === editData.tenantCode)
			: all;

		if (!editData) return sameTenant;

		const blocked = new Set<number>([editData.extractionConfigurationID]);
		let grew = true;
		while (grew) {
			grew = false;
			for (const c of sameTenant) {
				if (
					c.parentConfigurationID != null &&
					blocked.has(c.parentConfigurationID) &&
					!blocked.has(c.extractionConfigurationID)
				) {
					blocked.add(c.extractionConfigurationID);
					grew = true;
				}
			}
		}
		return sameTenant.filter((c) => !blocked.has(c.extractionConfigurationID));
	})();

	// Only fetch tenants if user is admin AND dialog is open
	const tenantsQuery = useTenantsQuery(isAdmin && open);

	useEffect(() => {
		if (editData) {
			setName(editData.name);
			setPrompt(editData.prompt);
			setJsonSchema(editData.jsonSchema);
			setIdentificationHints(editData.identificationHints || "");
			setConnectorConfigurationId(editData.connectorConfigurationID);
			setPageLimit(editData.pageLimit ? editData.pageLimit.toString() : "");
			setForceOcr(editData.forceOcr);
			setIsFallback(editData.isFallback);
			setParentConfigurationId(editData.parentConfigurationID ?? "");

			// Initialize tenant ID only once per dialog opening
			if (!tenantIdSet && editData.tenantCode && tenantsQuery.data?.items) {
				const tenant = tenantsQuery.data.items.find(
					(t) => t.code === editData.tenantCode,
				);
				if (tenant) {
					setTenantId(tenant.tenantID);
					setTenantIdSet(true);
				}
			}
		} else {
			setName("");
			setPrompt("");
			setJsonSchema("");
			setIdentificationHints("");
			setConnectorConfigurationId("");
			setTenantId("");
			setPageLimit("");
			setForceOcr(true);
			setIsFallback(false);
			setParentConfigurationId("");
			setTenantIdSet(false);
		}
		setErrors({
			name: "",
			prompt: "",
			jsonSchema: "",
			connectorConfigurationId: "",
			tenantId: "",
		});
	}, [editData, open, tenantsQuery.data, tenantIdSet]);

	const handleClose = () => {
		setName("");
		setPrompt("");
		setJsonSchema("");
		setIdentificationHints("");
		setConnectorConfigurationId("");
		setTenantId("");
		setPageLimit("");
		setForceOcr(true);
		setIsFallback(false);
		setParentConfigurationId("");
		setErrors({
			name: "",
			prompt: "",
			jsonSchema: "",
			connectorConfigurationId: "",
			tenantId: "",
		});
		onClose();
	};

	const handleSubmit = async () => {
		// Validation
		const newErrors = {
			name: !name.trim() ? "Name is required" : "",
			prompt: !prompt.trim() ? "Prompt is required" : "",
			jsonSchema: !jsonSchema.trim() ? "JSON Schema is required" : "",
			connectorConfigurationId: !connectorConfigurationId
				? "Connector Configuration is required"
				: "",
			tenantId: isAdmin && !tenantId ? "Tenant is required for admins" : "",
		};

		setErrors(newErrors);

		if (Object.values(newErrors).some((error) => error)) {
			return;
		}

		try {
			let result: any;
			if (isEditMode && editData) {
				result = await updateMutation.mutateAsync({
					id: editData.extractionConfigurationID,
					command: {
						name: name.trim(),
						prompt: prompt.trim(),
						jsonSchema: jsonSchema.trim(),
						connectorConfigurationId: connectorConfigurationId as number,
						identificationHints: identificationHints.trim() || null,
						pageLimit: pageLimit ? Number.parseInt(pageLimit, 10) : null,
						forceOcr,
						isFallback,
						parentConfigurationId: parentConfigurationId === "" ? null : (parentConfigurationId as number),
						tenantId: isAdmin && tenantId ? (tenantId as number) : null,
					},
				});
			} else {
				result = await createMutation.mutateAsync({
					name: name.trim(),
					prompt: prompt.trim(),
					jsonSchema: jsonSchema.trim(),
					connectorConfigurationId: connectorConfigurationId as number,
					identificationHints: identificationHints.trim() || null,
					pageLimit: pageLimit ? Number.parseInt(pageLimit, 10) : null,
					forceOcr,
					isFallback,
					parentConfigurationId: parentConfigurationId === "" ? null : (parentConfigurationId as number),
					tenantId: isAdmin && tenantId ? (tenantId as number) : null,
				});
			}

			if (result.status === 409 && result.data?.errors) {
				// Extract error message from the errors object
				const errorMessages = Object.values(result.data.errors)
					.flat()
					.map((err: any) => err.message)
					.filter(Boolean);

				if (errorMessages.length > 0) {
					setErrors((prevErrors) => ({
						...prevErrors,
						name: errorMessages[0],
					}));
					return;
				}
			}

			handleClose();
		} catch (err) {
			setErrors((prevErrors) => ({
				...prevErrors,
				name: `Failed to ${isEditMode ? "update" : "create"} extraction configuration. Please try again.`,
			}));
		}
	};

	const connectorConfigs: ConnectorConfigurationListItemDto[] =
		connectorConfigsQuery.data?.items || [];
	const tenants: TenantListItemDto[] = tenantsQuery.data?.items || [];
	const isLoading = createMutation.isPending || updateMutation.isPending;

	return (
		<>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="md"
				fullWidth
				disableEnforceFocus
			>
				<DialogTitle>
					{isEditMode
						? "Edit Extraction Configuration"
						: "Create Extraction Configuration"}
				</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<TextField
							autoFocus
							label="Name"
							type="text"
							fullWidth
							variant="outlined"
							value={name}
							onChange={(e) => {
								setName(e.target.value);
								setErrors({ ...errors, name: "" });
							}}
							error={!!errors.name}
							helperText={errors.name}
							placeholder="Enter configuration name"
						/>

						{isAdmin && (
							<FormControl fullWidth error={!!errors.tenantId}>
								<InputLabel>Tenant</InputLabel>
								<Select
									value={tenantId}
									label="Tenant"
									onChange={(e) => {
										setTenantId(e.target.value as number);
										setErrors({ ...errors, tenantId: "" });
									}}
								>
									{tenants.map((tenant) => (
										<MenuItem key={tenant.tenantID} value={tenant.tenantID}>
											{tenant.code}
										</MenuItem>
									))}
								</Select>
								{errors.tenantId && (
									<FormHelperText>{errors.tenantId}</FormHelperText>
								)}
							</FormControl>
						)}

						<FormControl fullWidth>
							<InputLabel>Parent configuration</InputLabel>
							<Select
								value={parentConfigurationId}
								label="Parent configuration"
								onChange={(e) =>
									setParentConfigurationId(
										e.target.value === "" ? "" : (e.target.value as number),
									)
								}
							>
								<MenuItem value="">
									<em>None (root)</em>
								</MenuItem>
								{parentOptions.map((config) => (
									<MenuItem
										key={config.extractionConfigurationID}
										value={config.extractionConfigurationID}
									>
										{config.parentName
											? `${config.parentName} › ${config.name}`
											: config.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormControl fullWidth error={!!errors.connectorConfigurationId || connectorIsDeleted}>
							<InputLabel>Connector Configuration</InputLabel>
							<Select
								value={connectorConfigurationId}
								label="Connector Configuration"
								onChange={(e) => {
									setConnectorConfigurationId(e.target.value as number);
									setErrors({ ...errors, connectorConfigurationId: "" });
								}}
							>
								{/* Konfigurace muze viset na smazanem konektoru - ten uz v nabidce neni,
								    takze bez tohoto by pole zustalo prazdne a nebylo by poznat proc.
								    Polozka je zakazana, takze zpatky na nej prepnout nejde. */}
								{connectorIsDeleted && (
									<MenuItem value={connectorConfigurationId} disabled>
										(deleted connector — pick another)
									</MenuItem>
								)}
								{connectorConfigs.map((config) => (
									<MenuItem
										key={config.connectorConfigurationID}
										value={config.connectorConfigurationID}
									>
										{config.provider} - {config.endpoint}
									</MenuItem>
								))}
							</Select>
							{connectorIsDeleted && (
								<FormHelperText>
									The connector this configuration used has been deleted. Pick an
									active one before saving.
								</FormHelperText>
							)}
							{errors.connectorConfigurationId && (
								<FormHelperText>
									{errors.connectorConfigurationId}
								</FormHelperText>
							)}
						</FormControl>

						<TextField
							label="Prompt"
							multiline
							rows={4}
							fullWidth
							variant="outlined"
							value={prompt}
							onChange={(e) => {
								setPrompt(e.target.value);
								setErrors({ ...errors, prompt: "" });
							}}
							error={!!errors.prompt}
							helperText={errors.prompt}
							placeholder="Enter extraction prompt"
						/>

						<Stack direction="column" spacing={0.5}>
							<Button
								variant="outlined"
								onClick={() => setJsonEditorOpen(true)}
								fullWidth
								sx={{ py: 1.5 }}
							>
								{jsonSchema ? "Edit JSON Schema" : "Add JSON Schema"}
							</Button>
							{errors.jsonSchema && (
								<FormHelperText error>{errors.jsonSchema}</FormHelperText>
							)}
						</Stack>

						<TextField
							label="Identification Hints (Optional)"
							multiline
							rows={3}
							fullWidth
							variant="outlined"
							value={identificationHints}
							onChange={(e) => setIdentificationHints(e.target.value)}
							placeholder="Enter identification hints (optional)"
						/>

						<TextField
							label="Page Limit (Optional)"
							type="number"
							fullWidth
							variant="outlined"
							value={pageLimit}
							onChange={(e) => setPageLimit(e.target.value)}
							placeholder="Enter page limit (optional)"
						/>

						<FormControlLabel
							control={
								<Checkbox
									checked={forceOcr}
									onChange={(e) => setForceOcr(e.target.checked)}
									color="primary"
								/>
							}
							label="Force OCR"
						/>

						<FormControlLabel
							control={
								<Checkbox
									checked={isFallback}
									onChange={(e) => setIsFallback(e.target.checked)}
									color="primary"
								/>
							}
							label="Fallback configuration"
						/>
						<FormHelperText sx={{ mt: -1.5, ml: 4 }}>
							Used when a document cannot be identified. A tenant has one
							fallback configuration - turning this on takes the flag from
							whichever configuration currently holds it.
						</FormHelperText>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="inherit">
						Cancel
					</Button>
					<Button
						onClick={handleSubmit}
						variant="contained"
						color="primary"
						disabled={isLoading}
					>
						{isLoading
							? isEditMode
								? "Updating..."
								: "Creating..."
							: isEditMode
								? "Update"
								: "Create"}
					</Button>
				</DialogActions>
			</Dialog>

			<JsonEditorDialog
				open={jsonEditorOpen}
				title="JSON Schema"
				value={jsonSchema}
				onClose={() => setJsonEditorOpen(false)}
				onSave={(value) => {
					setJsonSchema(value);
					setErrors({ ...errors, jsonSchema: "" });
				}}
			/>
		</>
	);
};
